import { GridColDef } from "@mui/x-data-grid";
import { Button, IconButton } from "@mui/material"; // Ou sua importação específica
import ItemStatus from "../../../../../../common/components/item_status";
import dataUtils from "../../../../../../common/utils/dataUtils";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";

type AllowedColors =
  | "green"
  | "red"
  | "blue"
  | "yellow"
  | "gray"
  | "light-blue";

interface StatusProps {
  title: string;
  color: AllowedColors;
  description: string;
}

interface Props {
  onActionEditar: (id: number, open: boolean) => void;
  onActionDeletar: (id: number, open: boolean) => void;
  onActionExport: (id: number) => void;
}

export const GenerateMedicaoColumns = ({
  onActionEditar,
  onActionDeletar,
  onActionExport,
}: Props): GridColDef[] => {
  // --- LÓGICA 1: STATUS (SITUAÇÃO) ---
  const getStatusProps = (status: number): StatusProps => {
    switch (status) {
      case 0: // EM ELABORACAO
        return { title: "E", color: "gray", description: "Em elaboração" };
      case 1: // FINALIZADA
        return { title: "F", color: "yellow", description: "Finalizada" };
      case 3: // APROVADAS
        return { title: "A", color: "green", description: "Aprovada" };
      case 4: // REPROVADAS
        return { title: "R", color: "red", description: "Reprovada" };
      default:
        return { title: "E", color: "gray", description: "Em elaboração" };
    }
  };

  // --- LÓGICA 2: PAGAMENTO PROVISIONADO ---
  const getPagamentoProps = (pgtoProvisionado: boolean): StatusProps => {
    if (pgtoProvisionado) {
      return {
        title: "PP",
        color: "green",
        description: "Pagamento provisionado",
      };
    } else {
      return {
        title: "NP",
        color: "gray",
        description: "Pagamento não provisionado",
      };
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return [
    {
      field: "Status",
      headerName: "Status",
      width: 70,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const { title, color, description } = getStatusProps(params.value);
        return (
          <div className="w-full h-full flex justify-center items-center">
            <ItemStatus title={title} color={color} description={description} />
          </div>
        );
      },
    },
    {
      field: "PgtoProvisionado",
      headerName: "Pagamento Provisionado",
      width: 100, 
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const { title, color, description } = getPagamentoProps(params.value);
        return (
          <div className="w-full h-full flex justify-center items-center">
            <ItemStatus title={title} color={color} description={description} />
          </div>
        );
      },
    },
    {
      field: "NrMedicaoTerceirizacao",
      headerName: "Nº",
      width: 60,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <div className="w-full h-full flex justify-center items-center">
          <span>{params.value}</span>
        </div>
      ),
    },
    {
      field: "NumeroContrato",
      headerName: "Contrato",
      width: 100,
      renderCell: (params) => {
        return (
          <div className="w-full h-full flex justify-start items-center text-left text-xs py-2 px-2">
            <span>{params.value}</span>
          </div>
        );
      },
    },
    {
      field: "NomeFornecedor",
      headerName: "Fornecedor",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        return (
          <div className="break-normal w-full h-full flex justify-start items-center text-left text-xs py-2 px-2">
            <span>{params.value}</span>
          </div>
        );
      },
    },
    {
      field: "NomeCentroCusto",
      headerName: "Centro de Custo",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        return (
          <div className="break-normal w-full h-full flex justify-start items-center text-left text-xs py-2 px-2">
            <span>{params.value}</span>
          </div>
        );
      },
    },
    {
      field: "Descricao",
      headerName: "Descrição",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        return (
          <div className="break-normal w-full h-full flex justify-start items-center text-left text-xs py-2 px-2">
            <span>{params.value}</span>
          </div>
        );
      },
    },
    {
      field: "NomeFuncionario",
      headerName: "Responsável pela Mediçao",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => {
        return (
          <div className="break-normal w-full h-full flex justify-start items-center text-left text-xs py-2 px-2">
            <span>{params.value}</span>
          </div>
        );
      },
    },
    {
      field: "ValorTotalCalculado",
      headerName: "Valor da Medição",
      width: 120,
      renderCell: (params) => {
        const vlr = params.row.ValorTotalCalculado || 0;
        return (
          <div className="w-full h-full flex justify-center items-center text-center text-xs py-2">
            <span>{formatCurrency(vlr)}</span>
          </div>
        );
      },
    },
    {
      field: "DataDe",
      headerName: "Início",
      width: 100,
      renderCell: (params) => {
        return (
          <div className="w-full h-full flex justify-center items-center text-center text-xs py-2">
            <span>
              {dataUtils.dataFormatada(params.value ? params.value : null)}
            </span>
          </div>
        );
      },
    },
    {
      field: "DataAte",
      headerName: "Término",
      width: 100,
      renderCell: (params) => {
        return (
          <div className="w-full h-full flex justify-center items-center text-center text-xs py-2">
            <span>
              {dataUtils.dataFormatada(params.value ? params.value : null)}
            </span>
          </div>
        );
      },
    },
    {
      field: "Exportar",
      filterable: false,
      headerName: "Exportar",
      sortable: false,
      width: 100,
      valueGetter: (_, row) => {
        return row["IdMedicaoTerceirizacao"] as number;
      },
      renderCell: ({ value }) => {
        return (
          <div className="w-full h-full flex justify-center items-center">
            <Button
              onMouseDown={(e) => {
                if (e.button === 1) {
                  onActionExport(value);
                } else {
                  onActionExport(value);
                }
                e.preventDefault();
              }}
              sx={{
                fontSize: 11,
                paddingX: ".4rem",
                paddingY: 0,
                background: "#e6e6e6",
                borderLeft: ".2rem solid #45b6af",
                borderRadius: 0,
                color: "black",
                fontWeight: "normal",
              }}
            >
              Exportar
            </Button>
          </div>
        );
      },
    },
    {
      field: "IdMedicaoTerceirizacao",
      headerName: "Ações",
      sortable: false,
      filterable: false,
      width: 100,
      renderCell: (params) => (
        <div className="flex items-center justify-center w-full h-full gap-2">
          <IconButton
            onClick={() => onActionEditar(params.value, true)}
            title="Editar"
          >
            <DynamicIcon
              nameIcon="EditRounded"
              size={20}
              className="text-blue-500"
            />
          </IconButton>
          <IconButton
            onClick={() => onActionDeletar(params.value, true)}
            title="Excluir"
          >
            <DynamicIcon
              nameIcon="DeleteForeverRounded"
              size={20}
              className="text-red-500"
            />
          </IconButton>
        </div>
      ),
    },
  ];
};
